/**
 * MovingAverageChart Component
 * Used in: Dashboard screen - displays 90-Day/3-Year moving average analysis for financial metrics
 */
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FinancialData, getDataByViewMode } from '../data/financialData';

interface MovingAverageData {
  month: string;
  actualValue: number;
  sma90: number | null;
  dataPointsUsed: number;
  trend: 'up' | 'down' | 'neutral';
}

interface DataSeries {
  key: keyof FinancialData;
  name: string;
  color: string;
}

const dataSeries: DataSeries[] = [
  { key: 'totalIncome', name: 'Total Income', color: '#3b82f6' },
  { key: 'totalCOGS', name: 'Total COGS', color: '#ef4444' },
  { key: 'grossProfit', name: 'Gross Profit', color: '#10b981' },
  { key: 'totalExpense', name: 'Total Expense', color: '#f59e0b' },
  { key: 'netOrdinaryIncome', name: 'Net Income', color: '#8b5cf6' },
];

interface MovingAverageChartProps {
  viewMode?: 'monthly' | 'yearly';
  data: FinancialData[];
}

export const MovingAverageChart: React.FC<MovingAverageChartProps> = ({ viewMode = 'monthly', data }) => {
  const [selectedMetric, setSelectedMetric] = useState<keyof FinancialData>('netOrdinaryIncome');
  const [smaWindow] = useState(viewMode === 'yearly' ? 3 : 90); // 3-year or 90-day simple moving average
  
  // Get data based on view mode
  const chartData = useMemo(() => getDataByViewMode(viewMode, data), [viewMode, data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate 120-day Simple Moving Average
  const calculateSMA = (data: number[], window: number, index: number): number | null => {
    if (index < window - 1) return null; // Not enough data points
    
    const slice = data.slice(index - window + 1, index + 1);
    const sum = slice.reduce((acc, val) => acc + val, 0);
    return sum / slice.length;
  };

  // Transform data for line chart with moving average
  const movingAverageData: MovingAverageData[] = chartData.map((item, index) => {
    const actualValue = item[selectedMetric] as number;
    const allValues = chartData.map(d => d[selectedMetric] as number);
    
    // Calculate SMA (using available data if less than 90 points)
    const availableWindow = Math.min(smaWindow, index + 1);
    const sma90 = index >= 4 ? calculateSMA(allValues, availableWindow, index) : null; // Start SMA after 5 data points
    
    // Determine trend based on previous SMA value
    let trend: 'up' | 'down' | 'neutral' = 'neutral';
    if (index > 0 && sma90 !== null) {
      const prevSMA = calculateSMA(allValues, Math.min(smaWindow, index), index - 1);
      if (prevSMA !== null) {
        trend = sma90 > prevSMA ? 'up' : sma90 < prevSMA ? 'down' : 'neutral';
      }
    }

    return {
      month: item.month,
      actualValue,
      sma90,
      dataPointsUsed: availableWindow,
      trend
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = movingAverageData.find(d => d.month === label);
      if (!data) return null;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <div className="font-semibold text-gray-900 mb-2">{label}</div>
          <div className="space-y-1 text-sm">
            <div className="text-gray-700">Actual Value: {formatCurrency(data.actualValue)}</div>
            {data.sma90 && (
              <>
                <div className="text-blue-600 font-medium">
                  {viewMode === 'yearly' ? '3-Year' : '90-Day'} SMA: {formatCurrency(data.sma90)}
                </div>
                <div className="text-gray-500 text-xs">Data Points Used: {data.dataPointsUsed}</div>
                <div className={`text-xs font-medium ${
                  data.trend === 'up' ? 'text-green-600' : 
                  data.trend === 'down' ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  Trend: {data.trend === 'up' ? '↗ Rising' : data.trend === 'down' ? '↘ Falling' : '→ Neutral'}
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 lg:mb-0">
          {viewMode === 'yearly' ? '3-Year' : '90-Day'} Moving Average - {dataSeries.find(s => s.key === selectedMetric)?.name} ({viewMode === 'yearly' ? 'Yearly' : 'Monthly'})
        </h3>
        <div className="flex flex-wrap gap-2">
          {dataSeries.map((series) => (
            <button
              key={series.key}
              onClick={() => setSelectedMetric(series.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                selectedMetric === series.key
                  ? 'bg-gray-100 text-gray-900 border border-gray-200'
                  : 'bg-gray-50 text-gray-500 border border-gray-100 hover:bg-gray-100'
              }`}
            >
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: selectedMetric === series.key ? series.color : '#d1d5db' }}
              />
              {series.name}
            </button>
          ))}
        </div>
      </div>
      
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Moving Average Analysis:</span> 
          <span className="inline-flex items-center gap-1 ml-2">
            <div className="w-3 h-0.5 bg-gray-600"></div>
            Actual Values
          </span>
          <span className="inline-flex items-center gap-1 ml-4">
            <div className="w-3 h-0.5 bg-blue-600"></div>
            {viewMode === 'yearly' ? '3-Year' : '90-Day'} SMA
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Simple Moving Average smooths price action over {smaWindow} data points to identify trends
        </p>
      </div>

      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <LineChart data={movingAverageData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="month" 
            stroke="#6b7280"
            fontSize={10}
            angle={-45}
            textAnchor="end"
            height={60}
            interval="preserveStartEnd"
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={formatCurrency}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" />
          
          {/* Actual values line */}
          <Line 
            type="monotone" 
            dataKey="actualValue" 
            stroke="#6b7280" 
            strokeWidth={1.5}
            name="Actual Values"
            dot={false}
            connectNulls={false}
          />
          
          {/* Moving average SMA line */}
          <Line 
            type="monotone" 
            dataKey="sma90" 
            stroke="#2563eb" 
            strokeWidth={3}
            name={`${viewMode === 'yearly' ? '3-Year' : '90-Day'} SMA`}
            dot={false}
            connectNulls={false}
          />
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};